"use client"

/* eslint-disable react/no-unescaped-entities */
import ProductOptions from "@/components/LegacyMiniCart/productOptions"
import { useMiniCartStore } from "@/components/MiniCart/use-new-minicart-store"
import PreOrderText from "@/components/PreOrderText"
import Price from "@/components/Price"
import QuantityStepper from "@/components/QuantityStepper"
import { useIsPreorder } from "@/hooks/MiniCart/useIsPreorder"
import { formatString } from "@/lib/format-string"
import { getCartItemIsOutOfStock } from "@/lib/get-cart-item-is-out-of-stock"
import { cn } from "@/lib/utils"
import {
  Cart,
  CartItemInterface,
  ConfigurableProductOptions,
  ConfigurableVariant,
  Maybe,
  ProductInterface,
  SelectedConfigurableOption,
} from "@/types/graphql.prod"
import resourceUrl from "@/util/makeUrl"
import { Form } from "informed"
import { X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useMemo, useRef } from "react"
import { useRemoveItem } from "../use-remove-item"
import { useUpdateItemQuantity } from "../use-update-item-quantity"
import classes from "./cart-item.module.css"

export type ExtentProductVariants = ProductInterface & {
  variants?: Maybe<Array<Maybe<ConfigurableVariant>>>
  configurable_options?: Maybe<Array<Maybe<ConfigurableProductOptions>>>
}

// 1. 首先扩展 CartItemInterface
export type ExtendedCartItemInterface = Omit<CartItemInterface, "product"> & {
  configurable_options?: Maybe<Array<Maybe<SelectedConfigurableOption>>>
  product: ExtentProductVariants
}

// 2. 然后基于 Cart 类型创建新类型，只替换 items 属性
export type ExtendedCart = Omit<Cart, "items"> & {
  items?: Maybe<Array<Maybe<ExtendedCartItemInterface>>>
}

export function CartItem({ item }: { item: Maybe<ExtendedCartItemInterface> }) {
  const product = item?.product
  const uid = item?.uid as string
  const quantity = item?.quantity
  const select_product_image = item?.select_product_image
  const configurable_options = item?.configurable_options
  const prices = item?.prices
  const setOpen = useMiniCartStore((state) => state?.setDialogOpen)

  const { isPreOrder, note } = useIsPreorder({
    product,
    configurable_options,
  })
  const formApiRef = useRef(null)

  const { removeItemLoading: isDeleting, handleRemoveItem } = useRemoveItem()

  const { updateItemLoading, handleUpdateItemQuantity } = useUpdateItemQuantity(
    {
      resetToPrevQuantity: () => {
        formApiRef.current?.setValue("quantity", quantity)
      },
    }
  )

  const onUpdateItemQuantity = (quantity: number) => {
    handleUpdateItemQuantity(uid, quantity)
  }

  const itemLink = useMemo(
    () => resourceUrl(`/${product?.url_key}.html`),
    [product?.url_key]
  )

  const { isOutOfStock } = getCartItemIsOutOfStock({
    item: item,
  })

  return (
    <div
      className={cn(
        "pb-[20px] pt-[15px]",
        "opacity-100",
        "text-sm",
        "border-b-1",
        "border-solid",
        "border-[#EFEFEF]",
        "flex",
        isDeleting && "opacity-50",
        updateItemLoading && "opacity-50"
      )}
      data-cy="MiniCart-Item-root"
    >
      <Link
        className={"block h-[120px] w-[120px] "}
        href={itemLink}
        data-cy="item-thumbnailContainer"
        onClick={() => {
          setOpen(false)
        }}
      >
        <Image
          alt={product?.name ?? ""}
          width={120}
          height={120}
          src={
            select_product_image ||
            product?.thumbnail?.url ||
            "/product-image-placeholder.svg"
          }
          data-cy="Item-image"
        />
      </Link>
      <div className={cn("flex-1", "flex flex-col", "relative", "pl-[12px]")}>
        <div className="flex justify-between">
          <Link
            className={cn(
              "line-clamp-2 font-sans text-[16px] leading-[1.4] text-title",
              "max-w-[80%]"
            )}
            href={itemLink}
            data-cy="item-name"
            onClick={() => {
              setOpen(false)
            }}
          >
            {formatString(product?.name ?? "")}
          </Link>
          <span
            className={classes.deleteIcon}
            onClick={() => {
              handleRemoveItem(uid)
            }}
          />
        </div>

        {isOutOfStock && (
          <span className="capitalize text-red">out of stock</span>
        )}

        <PreOrderText isPreOrder={isPreOrder} note={note} />

        <ProductOptions options={configurable_options} />
        <div className="mt-[15px] flex items-end justify-between">
          <div className="">
            <Form
              initialValues={{
                quantity: quantity ?? 1,
              }}
              formApiRef={formApiRef}
            >
              <QuantityStepper
                min={0}
                initialValue={quantity ?? 1}
                // @ts-expect-error - Explain WHY you're ignoring this error
                onChange={onUpdateItemQuantity}
              />
            </Form>
          </div>

          <span
            data-cy="MiniCart-Item-price"
            className="font-poppins text-[16px] font-[600] text-[#2F2F2F]"
          >
            <Price
              currencyCode={prices?.price?.currency ?? "USD"}
              value={prices?.row_total?.value ?? 0}
            />
          </span>
        </div>
      </div>
    </div>
  )
}
